import { fabric } from "fabric";
import type { IGroupOptions } from "fabric/fabric-impl";
import { adjustElementTopWithPageBreak } from "../renderer/fabric-renderer-utils";
import { defaults } from "../state/defaults";
import type { ColumnAlignment } from "../state/types";
import { EllipsisTextbox } from "./ellipsis-textbox";
import { WordWrapTextbox } from "./WordWrapTextbox";

const defaultTableOptions: IGroupOptions = {
  lockSkewingY: true,
  lockScalingY: true,
  lockScalingFlip: true,
};

export class Table extends fabric.Group {
  id: string;
  headers: string[] = [];
  columnWidths: number[] = [];
  rows: string[][] = [[]];
  alignments: ColumnAlignment[] = [];
  type: string = "table";
  fontSize: number = defaults.fontSize;
  fontFamily: string = defaults.fontFamily;
  fontWeight: string | number = "bold";
  lineHeight: number = defaults.lineHeight;
  textAlign: string = defaults.textAlign;
  textColor: string = defaults.textColor;
  pageHeight: number = 0;
  pageBreakMargin: number = 0;
  data: { strokeStyle: string | undefined; lineWidth: number | undefined } = {
    strokeStyle: "",
    lineWidth: 0,
  };

  private readonly xPadding = 5;
  private readonly yPadding = 5;
  private readonly defaultBorderColor = "#000000";

  constructor(options?: IGroupOptions, id?: string) {
    super([], { ...options, ...defaultTableOptions }, true);

    this.id = id ?? new Date().getTime().toString();
    this.data = {
      strokeStyle: options?.data?.strokeStyle ?? this.defaultBorderColor,
      lineWidth: options?.data?.lineWidth ?? 0,
    };

    this.addEventListeners();
  }

  set<K extends keyof this>(
    key: K,
    value: this[K] | ((value: this[K]) => this[K]),
  ): fabric.Object;

  set(options: Partial<this>): fabric.Object;

  set<K extends keyof this>(
    keyOrOptions: K | Partial<this>,
    value?: this[K] | ((value: this[K]) => this[K]),
  ): fabric.Object {
    let groupObject: fabric.Object;
    if (typeof keyOrOptions === "object" && value === undefined) {
      groupObject = super.set(keyOrOptions);
    } else {
      groupObject = super.set(keyOrOptions as K, value as this[K]);
    }
    this.updateGrid();
    return groupObject;
  }

  updateGrid(): void {
    this.updateCellsPositions();
    this.renderBorders();
    this.setCoords();
  }

  addCells(
    headers: string[],
    rows: string[][],
    alignments: ColumnAlignment[] = [],
  ): void {
    this.remove(...this.getObjects());

    this.set("headers", headers);
    this.set("rows", rows);
    this.set("alignments", alignments);

    headers.forEach((content) => {
      this.add(
        new EllipsisTextbox(content, {
          fontSize: this.fontSize,
          fontFamily: this.fontFamily,
          fill: this.textColor,
          lineHeight: this.lineHeight,
          fontWeight: this.fontWeight,
          textAlign: this.textAlign,
          ellipsis: true,
        }),
      );
    });

    rows.flatMap((row) => {
      return row.forEach((content, indx) => {
        this.add(
          new WordWrapTextbox(content, {
            fontSize: this.fontSize,
            fontFamily: this.fontFamily,
            fontWeight: this.fontWeight,
            lineHeight: this.lineHeight,
            fill: this.textColor,
            textAlign: alignments[indx] ?? "left",
            ellipsis: true,
          }),
        );
      });
    });

    this.updateGrid();
  }

  getCells(): fabric.Object[] {
    return this.getObjects("textbox");
  }

  getHeaderCells(): fabric.Object[] {
    const columnsCount = this.headers.length;
    return this.getCells().slice(0, columnsCount);
  }

  getRowCells(): fabric.Object[] {
    const columnsCount = this.headers.length;
    return this.getCells().slice(columnsCount);
  }

  getCellsBorders(): fabric.Object[] {
    return this.getObjects("rect");
  }

  private updateCellsPositions(): void {
    if (!this.width) {
      return;
    }

    if (!this.headers || !this.rows) {
      return;
    }

    const { lineWidth } = this.data ?? {};
    const hasHeaders = this.headers.length ? 1 : 0;
    const rowsCount = this.rows.length + hasHeaders;
    const columnsCount = this.headers.length;

    const cells = this.getCells();

    updateCellsWidths({
      cells,
      columnsCount,
      containerWidth: this.width,
      columnWidths: this.columnWidths,
      xPadding: this.xPadding,
    });

    const rowHeights = getRowsHeights({
      cells,
      columnsCount,
      rowsCount,
    });

    this.top = getContainerTopWithPageBreak(
      this.top ?? 0,
      rowHeights[0],
      this.pageHeight,
      this.pageBreakMargin,
    );

    this.height = getContainerHeight(rowHeights, this.yPadding, lineWidth);

    const accumulatedHeight = updateCellsTopWithPageBreak({
      cells,
      columnsCount,
      rowHeights,
      containerTop: this.top ?? 0,
      containerHeight: this.height,
      pageHeight: this.pageHeight,
      pageBreakMargin: this.pageBreakMargin,
      yPadding: this.yPadding,
    });

    const heightDiff = accumulatedHeight - this.height;
    if (heightDiff) {
      this.height = accumulatedHeight;
      cells.forEach((obj: fabric.Object) => {
        obj.top = (obj.top ?? 0) - heightDiff / 2;
      });
    }
  }

  private renderBorders(): void {
    if (!this.headers || !this.columnWidths || !this.rows) {
      return;
    }

    const { strokeStyle = this.defaultBorderColor, lineWidth = 0 } =
      this.data ?? {};

    const columnsCount = this.headers.length;
    const totalColumnWidth = getTotalColumnWidth(this.columnWidths);
    const columnUnitWidth = totalColumnWidth
      ? (this.width ?? 0) / totalColumnWidth
      : 0;

    const cells = this.getCells();

    cells.forEach((cell: fabric.Object, index: number) => {
      const columnIndex = index % columnsCount;
      const columnWidth = this.columnWidths[columnIndex] * columnUnitWidth;
      const isLastColumn = columnIndex === columnsCount - 1;

      const left = (cell.left ?? 0) - this.xPadding;
      const width = columnWidth - (isLastColumn ? lineWidth * 2 : lineWidth);
      const top = (cell.top ?? 0) - this.yPadding;
      const height = (cell.height ?? 0) - lineWidth + this.yPadding * 2;

      const borderIndex = index + cells.length;

      renderCellBorder({
        table: this,
        borderIndex,
        left,
        top,
        width,
        height,
        lineWidth,
        strokeStyle,
      });
    });
  }

  private addEventListeners(): void {
    this.on("scaling", () => this.updateGrid());
    this.on("removed", () => this.off("scaling"));
  }
}

const updateCellsWidths = (params: {
  cells: fabric.Object[];
  columnsCount: number;
  containerWidth: number;
  xPadding: number;
  columnWidths: number[];
}): void => {
  const { cells, columnsCount, columnWidths, containerWidth, xPadding } =
    params;

  const totalColumnWidth = getTotalColumnWidth(columnWidths);
  const columnUnitWidth = totalColumnWidth
    ? containerWidth / totalColumnWidth
    : 0;

  const occupiedUnitsByColumn = columnWidths.reduce<number[]>(
    (acc: number[], columnWidth: number, index: number) => {
      const previousOccupiedUnits = index === 0 ? 0 : acc[index - 1];
      acc[index] = columnWidth + previousOccupiedUnits;

      return acc;
    },
    Array(columnsCount).fill(0),
  );

  cells.forEach((obj: fabric.Object, index: number) => {
    const columnIndex = index % columnsCount;
    const columnWidth = columnWidths[columnIndex] * columnUnitWidth;

    if (containerWidth) {
      const unitsToMove =
        columnIndex === 0 ? 0 : occupiedUnitsByColumn[columnIndex - 1];
      const containerCenter = containerWidth / 2;

      obj.set({
        left: unitsToMove * columnUnitWidth - containerCenter + xPadding,
        width: Math.max(0, columnWidth - xPadding * 2),
      });
    }
  });
};

const getRowsHeights = (params: {
  cells: fabric.Object[];
  columnsCount: number;
  rowsCount: number;
}): number[] => {
  const { cells, columnsCount, rowsCount } = params;

  const rowHeights: number[] = Array(rowsCount).fill(0);

  cells.forEach((obj: fabric.Object, index: number) => {
    const rowIndex = Math.floor(index / columnsCount);

    if (obj.height && obj.height > rowHeights[rowIndex]) {
      rowHeights[rowIndex] = obj.height;
    }
  });

  return rowHeights;
};

const updateCellsTopWithPageBreak = (params: {
  cells: fabric.Object[];
  columnsCount: number;
  rowHeights: number[];
  containerTop: number;
  containerHeight: number;
  pageHeight: number;
  pageBreakMargin: number;
  yPadding: number;
}): number => {
  const {
    cells,
    columnsCount,
    rowHeights,
    containerTop,
    containerHeight,
    pageHeight,
    yPadding,
    pageBreakMargin,
  } = params;

  let accumulatedHeight = 0;
  cells.forEach((obj: fabric.Object, index: number) => {
    const rowIndex = Math.floor(index / columnsCount);
    const columnIndex = index % columnsCount;
    const isLastColumn = columnIndex === columnsCount - 1;
    const containerCenter = containerHeight / 2;

    if (containerHeight) {
      obj.set({
        top: accumulatedHeight - containerCenter + yPadding,
        height: rowHeights[rowIndex],
      });
    }

    const firstPageHeight = pageHeight - containerTop;

    if (isLastColumn) {
      accumulatedHeight += rowHeights[rowIndex] + yPadding * 2;
      const nextRowHeight = rowHeights[rowIndex + 1] + yPadding;

      // Check if accumulated height causes page break
      if (nextRowHeight) {
        const activePage =
          !pageHeight || accumulatedHeight < firstPageHeight
            ? 0
            : Math.floor((accumulatedHeight - firstPageHeight) / pageHeight) +
              1;

        const nextPageHeight = firstPageHeight + activePage * pageHeight;

        const hitsPageBreak =
          accumulatedHeight + (nextRowHeight - yPadding) >
          nextPageHeight - pageBreakMargin;

        if (hitsPageBreak) {
          accumulatedHeight = nextPageHeight + pageBreakMargin;
        }
      }
    }
  });

  return accumulatedHeight;
};

const getContainerTopWithPageBreak = (
  containerTop: number,
  firstRowHeight: number,
  pageHeight: number,
  pageBreakMargin: number,
): number => {
  return adjustElementTopWithPageBreak(
    containerTop,
    firstRowHeight,
    pageHeight,
    pageBreakMargin,
  );
};

const getContainerHeight = (
  rowHeights: number[],
  yPadding: number,
  lineWidth: number | undefined = 0,
): number => {
  return (
    rowHeights.reduce(
      (acc, rowHeight) => acc + rowHeight + yPadding * 2,
      lineWidth,
    ) || 10
  );
};

interface CellBorder {
  table: Table;
  borderIndex: number;
  left: number;
  top: number;
  width: number;
  height: number;
  lineWidth: number | undefined;
  strokeStyle: string | undefined;
}

const renderCellBorder = ({
  table,
  borderIndex,
  left,
  top,
  width,
  height,
  lineWidth = 0,
  strokeStyle,
}: CellBorder): fabric.Rect | undefined => {
  const rectProperties: fabric.IRectOptions = {
    left,
    top,
    width: width + lineWidth,
    height: height + lineWidth,
    fill: "transparent",
    stroke: strokeStyle,
    strokeWidth: lineWidth,
    selectable: false,
    originX: "left",
    originY: "top",
    evented: false,
  };

  const currentRect = table.getObjects()[borderIndex];
  if (currentRect) {
    currentRect.set(rectProperties);
    return;
  }

  const newCurrentRect = new fabric.Rect(rectProperties);
  table.add(newCurrentRect);
};

const getTotalColumnWidth = (columnWidths: number[]): number => {
  return columnWidths.reduce((acc, colSize) => acc + colSize, 0);
};
